// components/Hero.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, TrendingUp, Award, Users } from 'lucide-react';
import { Stat } from '../types';

interface HeroProps {
  stats?: Stat[];
}

const Hero: React.FC<HeroProps> = ({ 
  stats = [
    { value: '100', suffix: '+', label: 'Projektia toteutettu' },
    { value: '50', suffix: '+', label: 'Tyytyväistä asiakasta' },
  ]
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);


  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                             linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`
          }}
        />
        
        {/* Floating Particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}

        {/* Gradient Orbs with Parallax */}
        <div 
          className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-3xl opacity-20 animate-pulse"
          style={{
            transform: `translate(${mousePosition.x * 2}px, ${mousePosition.y * 2}px)`
          }}
        />
        <div 
          className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-3xl opacity-20 animate-pulse"
          style={{
            transform: `translate(${-mousePosition.x * 2}px, ${-mousePosition.y * 2}px)`,
            animationDelay: '2s'
          }}
        />
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-600 to-purple-600 rounded-full blur-3xl opacity-10"
          style={{
            transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px) translate(-50%, -50%)`
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          {/* Animated Badge */}
          <div 
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-full mb-8 border border-white/20 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <Star className="w-5 h-5 text-yellow-400 animate-spin-slow" />
            <span className="text-white text-sm font-medium">Ideasta toimivaksi kokonaisuudeksi</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>

          {/* Main Heading with Animation */}
          <h1 
            className={`text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 transition-all duration-1000 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="block mb-2">Rakennamme</span>
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 animate-gradient-x">
                Tulevaisuutta
              </span>
              <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10">
                <path
                  d="M0,5 Q75,0 150,5 T300,5"
                  stroke="url(#gradient)"
                  strokeWidth="2"
                  fill="none"
                  className="animate-draw"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#60A5FA" />
                    <stop offset="50%" stopColor="#A78BFA" />
                    <stop offset="100%" stopColor="#F472B6" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h1>
          <p 
            className={`text-xl font-bold text-white/90 mb-2 transition-all duration-1000 delay-150 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            FennoFuture - Metalli- ja Muovituotteet & Suunnittelupalvelut
          </p>

          {/* Description */}
          <p 
            className={`text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            Ideasta toimivaan ratkaisuun – suunnittelusta toteutukseen.
            <br />
            Erikoistuminen metallituotteiden suunnitteluun ja valmistukseen sekä mekatroniikkakehitykseen.
          </p>

          {/* CTA Buttons with Hover Effects */}
          <div 
            className={`flex flex-col sm:flex-row gap-4 justify-center mb-16 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            
            <button 
              onClick={() => navigate('/yhteystiedot')}
              className="group relative border-2 border-white/50 text-white px-8 py-4 rounded-full font-semibold backdrop-blur-sm overflow-hidden transition-all duration-300 hover:border-white hover:scale-105">
              <span className="relative z-10">Ota yhteyttä</span>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
            </button>
          </div>

          {/* Animated Stats */}
          <div 
            className={`grid grid-cols-2 gap-4 md:gap-8 max-w-2xl mx-auto transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="group relative text-center p-4 rounded-2xl backdrop-blur-sm bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105"
              >
                <div className="text-3xl md:text-4xl font-bold text-white mb-1 flex items-center justify-center">
                  {stat.prefix}
                  <span className="inline-block animate-count">{stat.value}</span>
                  {stat.suffix}
                </div>
                <div className="text-sm md:text-base text-gray-300">{stat.label}</div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300" />
              </div>
            ))}
          </div>

          {/* Company Info Badge */}
          <div 
            className={`mt-12 inline-flex items-center gap-4 text-sm text-gray-400 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span className="flex items-center gap-1">
              <Award className="w-4 h-4" />
              Y-tunnus: 3203489-7
            </span>
            <span className="w-1 h-1 bg-gray-400 rounded-full" />
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              Perustettu 2021
            </span>
          </div>
        </div>
      </div>

      {/* Fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;